import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Api } from '../api/api';
import { AppText } from '../components/AppText';
import { AppTextField } from '../components/AppTextField';
import { InlineNotice } from '../components/InlineNotice';
import { PrimaryButton } from '../components/PrimaryButton';
import { PublicRoute } from '../navigation/routes';
import { Strings } from '../strings';

/**
 * The page a reset link opens.
 *
 * **It is a page, not a screen, and it lives here rather than under `screens/` for a reason.** The three
 * clients share a screen inventory that `check-parity.sh` enforces; this is not part of it. The apps do not
 * grow their own reset screen — they open this page — so it is a genuine web-only surface and is recorded
 * as divergence 18 in the parity register rather than smuggled past the check.
 *
 * The copy is nevertheless in all three clients' string files, because it is product vocabulary and a
 * native screen later should cost a layout rather than a translation round.
 *
 * Three states, and the middle one is the one that matters: no token at all, a token being used, and a
 * token the API rejected. A link that has expired or been used already must say so plainly — a parent who
 * clicks an old mail should be told to ask for a new one, not shown a form that fails on submit.
 */
export function ResetPasswordPage() {
  const [parameters] = useSearchParams();
  const navigate = useNavigate();
  const token = parameters.get('token');

  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDone, setIsDone] = useState(false);

  const canSubmit = token !== null && password.length > 0 && !isSubmitting;

  const submit = async (): Promise<void> => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    setError(null);

    const result = await Api.resetPassword(token, password);

    setIsSubmitting(false);

    if (result.ok) {
      setIsDone(true);

      return;
    }

    // The API's own message, as it came. It is the one that knows whether the password was too short or
    // the token was spent, and it says so in reviewed Slovak (BR-13).
    setError(result.error.message);
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      style={{ display: 'flex', flexDirection: 'column', padding: '0 var(--space-6)' }}
    >
      <AppText as="h1" style="titleL">
        {Strings.resetPasswordTitle}
      </AppText>

      {token === null ? (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <InlineNotice text={Strings.resetPasswordInvalid} tone="error" />
        </div>
      ) : isDone ? (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <InlineNotice text={Strings.resetPasswordDone} tone="info" />
        </div>
      ) : (
        <>
          <AppText
            color="var(--text-secondary)"
            sx={{ marginTop: 'var(--space-2)', marginBottom: 'var(--space-6)' }}
          >
            {Strings.resetPasswordBody}
          </AppText>

          {error !== null ? (
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <InlineNotice text={error} tone="error" />
            </div>
          ) : null}

          <AppTextField
            label={Strings.labelNewPassword}
            value={password}
            onChange={setPassword}
            isPassword
            autoComplete="new-password"
          />

          <div style={{ marginTop: 'var(--space-6)' }}>
            <PrimaryButton
              type="submit"
              label={Strings.actionSetPassword}
              enabled={canSubmit}
              loading={isSubmitting}
            />
          </div>
        </>
      )}

      <button
        type="button"
        onClick={() => navigate(PublicRoute.signIn)}
        style={{
          marginTop: 'var(--space-4)',
          background: 'none',
          border: 'none',
          padding: 0,
          textAlign: 'left',
          color: 'var(--action-primary)',
          fontSize: '15px',
          lineHeight: '20px',
          fontWeight: 600,
        }}
      >
        {Strings.actionSignIn}
      </button>
    </form>
  );
}
